import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import Typography from '@/components/core/Typography/Typography';
import { times } from 'lodash';
import { FlexCenter } from '@/components/Helpers';

const DigitInput = styled.input`
    border-radius: ${({ theme }) => theme.extras.radius.sm};
    width: 100%;
    max-width: 48px;
    height: 54px;
    box-sizing: content-box;
    text-align: center;
    transition: box-shadow 0.2s ease-in-out;

    font-size: ${({ theme }) => theme.typography.uiType['2xl'].S.size};
    line-height: ${({ theme }) => theme.typography.uiType['2xl'].S.lineHeight};
    letter-spacing: ${({ theme }) => theme.typography.uiType['2xl'].S.letterSpacing ?? 'normal'};
    font-weight: ${({ theme }) => theme.typography.weight.semiBold};
    border: none;
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.palette.grey_400};

    &:focus-visible {
        outline: none;
    }

    &:disabled {
        opacity: ${({ theme }) => theme.extras.opacity.disabled};
        cursor: default;
    }
`;

export const DigitsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 6px;
`;

const Hint = styled(Typography)`
    text-align: center;
`;

const Root = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing['3xs']};

    &:not([data-error]) {
        input:focus {
            box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.primary.focused};
        }

        input:not(:focus):hover {
            box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.palette.grey_500};
        }
    }

    &[data-error] input {
        box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.palette.red_100};
        color: ${({ theme }) => theme.colors.palette.red_300};
    }

    &[data-error] ${Hint} {
        color: ${({ theme }) => theme.colors.palette.red_300};
    }
`;

interface OtpInputProps {
    fieldCnt?: number;
    errored?: boolean;
    hint?: string;

    onSubmit(...args: unknown[]): unknown;

    clearError?(...args: unknown[]): unknown;
}

export default function OtpInput({
    onSubmit,
    errored = false,
    hint,
    fieldCnt = 6,
    clearError
}: OtpInputProps) {
    const refList = useRef(new Array(fieldCnt).fill(null) as (HTMLInputElement | null)[]);
    const [values, setValues] = useState(new Array<string>(fieldCnt as number).fill(''));
    const [focusIndex, setFocusIndex] = useState(0);

    useEffect(() => {
        refList.current[focusIndex]?.focus();
    }, [focusIndex]);

    useEffect(() => {
        if (errored && fieldCnt) {
            setValues(times(fieldCnt, () => ''));
            setFocusIndex(0);
        }
    }, [errored, fieldCnt]);

    const handleChange = (i: number, val: string) => {
        if (errored && fieldCnt && clearError) {
            clearError();
        }

        if (val.length > 0 && isNaN(Number(val))) {
            return;
        }

        const newValues = values.slice(0, i).concat(val.split('').length > 0 ? val.split('') : '').concat(values.slice(i + 1)).slice(0, fieldCnt as number);

        setValues(newValues);

        if (i < (fieldCnt as number) - 1 && val.length > 0 && val.length !== fieldCnt) {
            setFocusIndex(i + 1);
        }

        if (newValues.every(v => /[0-9]/.test(v))) {
            onSubmit(newValues.join(''));
        }
    };

    const handleFocus = (i: number) => {
        if (i > 0 && values.every(v => v.length === 0)) {
            refList.current[0]?.focus();
        }
    };

    const handleKeyDown = (i: number, key: string) => {
        if (key === 'Backspace' && values[i].length === 0 && i > 0) {
            setFocusIndex(i - 1);
        }
    };

    return (
        <Root data-error={errored ? true : undefined}>
            <FlexCenter>
                <DigitsContainer>
                    {/* eslint-disable-next-line react-hooks/refs */}
                    {refList.current.map((_, i) => (
                        <DigitInput
                          value={values[i]}
                          {...(i === 0 ? {
                              autoComplete: 'one-time-code',
                              pattern: '\\d+'
                          } : {
                              onFocus: () => handleFocus(i),
                              maxLength: 1,
                              pattern: '\\d'
                          })}
                          ref={el => {
                              refList.current[i] = el;
                          }}
                          key={i}
                          onChange={e => handleChange(i, e.target.value)}
                          onKeyDown={e => handleKeyDown(i, e.key)}
                        />
                    ))}
                </DigitsContainer>
            </FlexCenter>

            {hint && <Hint variant='uiType' modifier='R' size='xs'>{hint}</Hint>}
        </Root>
    );
}
